import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import VendorFormDialog from "@/components/VendorFormDialog";
import VendorPocDialog from "@/components/VendorPocDialog";
import StatBlock from "@/components/vendor/StatBlock";
import VendorSidePanel from "@/components/vendor/VendorSidePanel";
import VendorAttachmentsTabs from "@/components/vendor/VendorAttachmentsTabs";
import VendorCompliancePanel from "@/components/vendor/VendorCompliancePanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Started", "In-Progress", "Partial", "Completed", "AMC"];

function formatINR(v) {
  return `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [generalFiles, setGeneralFiles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [pocOpen, setPocOpen] = useState(false);
  const [editingPoc, setEditingPoc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [v, g, inv] = await Promise.all([
        api.get(`/vendors/${id}`),
        api.get(`/vendors/${id}/attachments`, { params: { kind: "general" } }),
        api.get(`/vendors/${id}/attachments`, { params: { kind: "invoice" } }),
      ]);
      setVendor(v.data);
      setGeneralFiles(g.data);
      setInvoices(inv.data);
    } catch {
      toast.error("Failed to load vendor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [id]);

  const changeStatus = async (s) => {
    try {
      const { data } = await api.put(`/vendors/${id}`, { status: s });
      setVendor(data);
      toast.success(`Status set to ${s}`);
    } catch {
      toast.error("Failed");
    }
  };

  const onEditSubmit = async (payload) => {
    try {
      const { data } = await api.put(`/vendors/${id}`, payload);
      setVendor(data);
      setEditOpen(false);
      toast.success("Vendor updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const removeVendor = async () => {
    if (!window.confirm("Delete this vendor and all attachments?")) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor deleted");
      navigate("/sales/vendors");
    } catch {
      toast.error("Delete failed");
    }
  };

  // PoCs
  const submitPoc = async (form) => {
    try {
      if (editingPoc) {
        await api.put(`/vendors/${id}/pocs/${editingPoc.id}`, form);
        toast.success("PoC updated");
      } else {
        await api.post(`/vendors/${id}/pocs`, form);
        toast.success("PoC added");
      }
      setPocOpen(false);
      setEditingPoc(null);
      load();
    } catch {
      toast.error("Failed to save PoC");
    }
  };
  const deletePoc = async (pocId) => {
    if (!window.confirm("Remove this PoC?")) return;
    try {
      await api.delete(`/vendors/${id}/pocs/${pocId}`);
      toast.success("PoC removed");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  // Attachments
  const uploadAttachment = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendors/${id}/attachments`, fd, {
        params: { kind },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    }
  };
  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/vendor-attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = att.file_name;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };
  const deleteAttachment = async (aid) => {
    try {
      await api.delete(`/vendor-attachments/${aid}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading || !vendor) {
    return (
      <div className="p-8 text-slate-500" data-testid="vendor-detail-loading">
        Loading…
      </div>
    );
  }

  const bal =
    Number(vendor.actual_value || 0) - Number(vendor.total_payments || 0);

  return (
    <div data-testid="vendor-detail-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate("/sales/vendors")}
            className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
            data-testid="back-to-vendors"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Vendor
            </div>
            <h1
              className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
              data-testid="vendor-detail-name"
            >
              {vendor.name}
            </h1>
          </div>
          <Select value={vendor.status} onValueChange={changeStatus}>
            <SelectTrigger
              className="w-44 bg-white border-slate-200 h-10"
              data-testid="vendor-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="edit-vendor-button"
          >
            <PencilSimple size={16} /> Edit
          </button>
          <button
            onClick={removeVendor}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-red-100 bg-white text-red-600 text-sm font-medium hover:bg-red-50"
            data-testid="delete-vendor-button"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBlock label="Bank Quote" value={formatINR(vendor.bank_quotation_value)} />
          <StatBlock label="Actual Value" value={formatINR(vendor.actual_value)} />
          <StatBlock
            label="Payments"
            value={formatINR(vendor.total_payments)}
            accent="text-emerald-700"
          />
          <StatBlock
            label={bal > 0 ? "Balance Due" : "Balance"}
            value={formatINR(bal)}
            accent={bal > 0 ? "text-amber-700" : "text-slate-500"}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <VendorSidePanel
            vendor={vendor}
            onAddPoc={() => {
              setEditingPoc(null);
              setPocOpen(true);
            }}
            onEditPoc={(p) => {
              setEditingPoc(p);
              setPocOpen(true);
            }}
            onDeletePoc={deletePoc}
          />

          <div className="lg:col-span-2 space-y-6">
            <VendorCompliancePanel vendor={vendor} onChange={load} />
            <VendorAttachmentsTabs
              generalFiles={generalFiles}
              invoices={invoices}
              onUpload={uploadAttachment}
              onDownload={downloadAttachment}
              onDelete={deleteAttachment}
            />
          </div>
        </div>
      </div>

      {editOpen && (
        <VendorFormDialog
          open={editOpen}
          initial={vendor}
          onClose={() => setEditOpen(false)}
          onSubmit={onEditSubmit}
        />
      )}
      {pocOpen && (
        <VendorPocDialog
          open={pocOpen}
          initial={editingPoc}
          onClose={() => {
            setPocOpen(false);
            setEditingPoc(null);
          }}
          onSubmit={submitPoc}
        />
      )}
    </div>
  );
}
